import { useSelector } from "react-redux";
import { selectCurrentUserClientID } from "../../features/auth/authSlice";
import { useEffect, useMemo, useState } from "react";
import { ProductAvailableTimeSummary } from "../../types/product";
import { useDynamicQueryData } from "../api/useDynamicQueryData";
import { selectSelectedProductDetails } from "../../features/guest/guestSlice";

const CATEGORY_ORDER = [
    "Hot Coffee Classics",
    "Iced Coffee Classics",
    "Capital Exclusives",
    "Refreshers",
    "Pastries",
];

interface useAvailableProductCartUseMemoProps {
    selectedCategories: string[];
    selectedSize: string[];
}

export const useAvailableProductCartUseMemo = ({
    selectedCategories,
    selectedSize,
}: useAvailableProductCartUseMemoProps) => {

    const clientId = useSelector(selectCurrentUserClientID);
    const productDetails = useSelector(selectSelectedProductDetails);
    const productID = productDetails.productID
    const packageID = productDetails.packageID
    const { chapelNameAndRemainingQty, availableProductOfClient, errorAvailableProductOfClient, isErrorAvailableProductOfClient, isLoadingAvailableProductOfClient, refetchAvailableProductOfClient, } = useDynamicQueryData({ clientId });
    const [now, setNow] = useState(new Date());

    /** Automatically update exactly when the next package starts or ends. */
    useEffect(() => {
        const packages = chapelNameAndRemainingQty?.packages;

        if (!packages?.length) return;

        const current = Date.now();

        const nextTime = packages
            .flatMap(pkg => [
                new Date(`${pkg.startDate}T${pkg.startTime}`).getTime(),
                new Date(`${pkg.endDate}T${pkg.endTime}`).getTime(),
            ])
            .filter(time => time > current)
            .sort((a, b) => a - b)[0];

        if (!nextTime) return;

        const timeout = window.setTimeout(async () => {
            setNow(new Date());

            await refetchAvailableProductOfClient();
        }, nextTime - current);

        return () => clearTimeout(timeout);

    }, [ chapelNameAndRemainingQty, refetchAvailableProductOfClient, now,]);

    /** * Packages that are active RIGHT NOW */
    const activePackageNames = useMemo(() => {
        const packages = chapelNameAndRemainingQty?.packages;

        if (!packages?.length) return [];

        return packages
            .filter((pkg) => {
                const start = new Date(`${pkg.startDate}T${pkg.startTime}`);
                const end = new Date(`${pkg.endDate}T${pkg.endTime}`);

                return now >= start && now <= end;
            })
            .map((pkg) => pkg.packageName);

    }, [chapelNameAndRemainingQty, now]);

    const errorMessage = useMemo(() => {
        if (!errorAvailableProductOfClient) return "";

        if ("data" in errorAvailableProductOfClient) {
            return (
                errorAvailableProductOfClient.data as {
                    message?: string;
                }
            )?.message ?? "";
        }

        if ("message" in errorAvailableProductOfClient) {
            return errorAvailableProductOfClient.message ?? "";
        }

        return "";
    }, [errorAvailableProductOfClient]);

    /** * Show products ONLY from active packages. */
    const filteredProducts = useMemo(() => {
        if (!availableProductOfClient?.data) return [];

        return availableProductOfClient.data.filter((product) => {

            const categoryMatch =
                selectedCategories.includes("All") ||
                selectedCategories.includes(product.categoryName);

            const sizeMatch =
                selectedSize.includes("All") ||
                selectedSize.includes(product.size);

            const packageMatch =
                activePackageNames.includes(product.packageName);

            return (
                product.isAvailable &&
                packageMatch &&
                categoryMatch &&
                sizeMatch
            );
        });

    }, [ availableProductOfClient?.data, activePackageNames, selectedCategories, selectedSize,]);

    const selectedProduct = useMemo(() => {
        if (!packageID || !productID) return null;

        return (
            filteredProducts.find(
                (product) =>
                    product.packageID === packageID &&
                    product.productID === productID
            ) ?? null
        );
    }, [filteredProducts, packageID, productID]);

    const groupedProducts = useMemo(() => {
        return filteredProducts.reduce<Record<string, ProductAvailableTimeSummary[]>>(
            (acc, product) => {
                if (!acc[product.categoryName]) {
                    acc[product.categoryName] = [];
                }

                acc[product.categoryName].push(product);

                return acc;
            },
            {}
        );
    }, [filteredProducts]);

    const sortedCategories = useMemo(() => {
        return Object.entries(groupedProducts).sort(([a], [b]) => {
            const indexA = CATEGORY_ORDER.indexOf(a);
            const indexB = CATEGORY_ORDER.indexOf(b);

            if (indexA !== -1 && indexB !== -1) {
                return indexA - indexB;
            }

            if (indexA !== -1) return -1;
            if (indexB !== -1) return 1;

            return a.localeCompare(b);
        });
    }, [groupedProducts]);

    return {
        availableProductOfClient,
        isLoadingAvailableProductOfClient,
        isErrorAvailableProductOfClient,
        errorMessage,

        filteredProducts,
        groupedProducts,
        sortedCategories,
        selectedProduct
    };
};